import { useConfirm } from 'material-ui-confirm'
import React, { useContext, useEffect } from 'react'
import { useBeforeUnload, usePromiseEvent } from '../../../../../../hooks'
import { useFieldArray, useForm } from 'react-hook-form'
import { BuildNavigationContext, PageBodyOverlayContext } from '../../..'
import { useDispatch, useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../../../../reducers'
import { BannerStatic } from '../../../../../../components'
import { skillBitePageBlockFormSchema } from '../../../../../../util/skill-bite-schemas'
import { yupResolver } from '@hookform/resolvers/yup'
import { unstable_usePrompt as usePrompt } from 'react-router-dom'
import { PAGE_BLOCK_TYPES } from '../../../utils/page-block-types'
import { ChevronRightRounded } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { nanoid } from '@reduxjs/toolkit'

const getBlocksWithDefaultValues = (blocks) => {
  const defaultBlocks = blocks.map(block => {
    if (block.type === PAGE_BLOCK_TYPES.DRAFTJS) {
      const hasEmptyBlocksAndEmptyEntityMap = !block.data || (block?.data?.blocks?.length === 0 && Object.keys(block?.data?.entityMap).length === 0)

      if (!hasEmptyBlocksAndEmptyEntityMap) return block

      return {
        ...block,
        data: {
          entityMap: {},
          blocks: [
            { key: nanoid(5), text: '', type: 'unstyled', depth: 0, inlineStyleRanges: [], entityRanges: [], data: {} }
          ]
        }
      }
    }

    if (block.type === PAGE_BLOCK_TYPES.TINYMCE) {
      if (!block.data || !block.data.html) return { ...block, data: { html: '' } }
      return block
    }

    if (block.type === PAGE_BLOCK_TYPES.COMMITMENT_CHECK) {
      const data = block.data || { labels: { yes: {}, no: {} } }

      return {
        ...block,
        data: {
          commitmentId: data.commitmentId ?? '',
          labels: {
            lastTimeCommitment: data.labels?.lastTimeCommitment ?? 'Last time, you committed to:',
            yes: {
              complimentaryText: data.labels?.yes?.complimentaryText ?? ', I nailed it!',
              title: data.labels?.yes?.title ?? 'Well done!',
              message: data.labels?.yes?.message ?? 'Good effort! Persevering with your commitments will really help to embed what you\'ve learned.',
              reviewLabel: data.labels?.yes?.reviewLabel ?? 'How did it go?'
            },
            no: {
              complimentaryText: data.labels?.no?.complimentaryText ?? ', not yet.',
              title: data.labels?.no?.title ?? 'That\'s OK.',
              message: data.labels?.no?.message ?? 'Think about what got in the way, and what you might do differently next time.',
              reviewLabel: data.labels?.no?.reviewLabel ?? 'What obstacles did you face?'
            }
          }
        }
      }
    }

    return block
  })

  return defaultBlocks
}

export function useSkillBitePageBlocksForm ({ skillBiteId, defaultValues, onAddBlockSuccess = () => { } }) {
  const confirm = useConfirm()
  const deleteBlock = usePromiseEvent('adminSkillBiteDeleteBlock')
  const addBlock = usePromiseEvent('adminSkillBiteInsertBlock')
  const updateBlocks = usePromiseEvent('adminSkillBiteUpdatePageBlocks')
  const pageBodyOverlayRef = useContext(PageBodyOverlayContext)
  const { pageBlocksFormRef } = useContext(BuildNavigationContext)
  const selectedPage = useSelector(SkillBiteEditSlice.selectors.selectSelectedPage)
  const selectedSession = useSelector(SkillBiteEditSlice.selectors.selectSelectedSession)
  const selectedSection = useSelector(SkillBiteEditSlice.selectors.selectSelectedSection)
  const skillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)
  const dispatch = useDispatch()
  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)

  const form = useForm({
    defaultValues: { ...defaultValues, blocks: getBlocksWithDefaultValues(defaultValues.blocks) },
    resolver: yupResolver(skillBitePageBlockFormSchema),
    mode: 'all'
  })

  // warns the user if they try to leave without saving the changes they made
  useBeforeUnload(() => form.formState.isDirty && canEdit)
  usePrompt({
    when: form.formState.isDirty && canEdit,
    message: 'You have unsaved changes on your page blocks form, are you sure you want to exit without saving?'
  })

  useEffect(() => {
    pageBlocksFormRef.current = form
  }, [form, pageBlocksFormRef])

  const handleDelete = ({ blockId, index }) => {
    if (!canEdit) return
    const blockToDelete = form.getValues().blocks[index]

    if (!blockToDelete) return

    if (blockToDelete.type === PAGE_BLOCK_TYPES.COMMITMENT) {
      // Check if the block is linked with a commitment check
      let commitmentCheckLink

      for (let i = 0; i < skillBite.sessions.length; i++) {
        const session = skillBite.sessions[i]
        for (let j = 0; j < session.sections.length; j++) {
          const section = session.sections[j]
          for (let k = 0; k < section.pages.length; k++) {
            const page = section.pages[k]
            for (let l = 0; l < page.blocks.length; l++) {
              const block = page.blocks[l]
              if (block.type === PAGE_BLOCK_TYPES.COMMITMENT_CHECK && block.data.commitmentId === blockToDelete.id) {
                commitmentCheckLink = {
                  blockId: block.id,
                  sessionId: session.id,
                  sessionName: session.name,
                  sectionId: section.id,
                  sectionName: section.name,
                  pageId: page.id,
                  pageName: 'Page ' + (k + 1)
                }
                break
              }
            }
          }
        }
      }

      if (commitmentCheckLink) {
        BannerStatic.show({
          label: (
            <Box display='flex' flexDirection='column' gap={2}>
              <Typography>You cannot delete this block because it is linked with a commitment check block on:</Typography>
              <Typography variant='caption'>{commitmentCheckLink.sessionName} <ChevronRightRounded fontSize='inherit' /> {commitmentCheckLink.sectionName}  <ChevronRightRounded fontSize='inherit' /> {commitmentCheckLink.pageName}</Typography>
              <Typography>Please unlink the commitment there and try again.</Typography>
            </Box>
          ),
          isWarning: true,
          autoHideDelay: 10 * 1000
        })
        console.log('Block is linked with a commitment check block', commitmentCheckLink)
        return
      }
    }

    confirm({
      title: 'Delete block',
      description: 'This is a destructive action. Are you sure you want to delete this block?',
      confirmationText: 'Yes, delete it.',
      confirmationButtonProps: {
        id: 'delete-block-confirmation-button',
        'data-test': 'delete-block-confirmation-button'
      },
      cancellationText: 'No, thanks.',
      cancellationButtonProps: {
        id: 'delete-block-cancellation-button',
        'data-test': 'delete-block-cancellation-button'
      },
      dialogProps: {
        PaperProps: {
          id: 'delete-block-confirmation-modal',
          'data-test': 'delete-block-confirmation-modal'
        }
      },
      titleProps: {
        id: 'delete-block-confirmation-title',
        'data-test': 'delete-block-confirmation-title'
      }
    }).then(async () => {
      pageBodyOverlayRef.current.open('Deleting block, please wait...')
      try {
        const payload = await deleteBlock({
          skillBiteId,
          sessionId: selectedSession.id,
          sectionId: selectedSection.id,
          pageId: selectedPage.id,
          blockId
        })
        BannerStatic.show({ label: 'Skill Bite Block has been successfully deleted' })
        pageBodyOverlayRef.current.close()
        dispatch(SkillBiteEditSlice.actions.deleteBlock(payload))

        if (selectedPage.id !== payload.pageId) return

        const currentBlocks = form.getValues().blocks
        const deletedBlock = currentBlocks[index]
        const wasFormDirty = form.formState.isDirty

        form.setValue(
          'blocks',
          currentBlocks
            .filter((block) => block.id !== blockId)
            .map((block) => ({ ...block, order: block.order > deletedBlock.order ? block.order - 1 : block.order }))
        )

        if (!wasFormDirty) {
          setTimeout(() => {
            form.reset(undefined, { keepValues: true, keepDirty: false, keepDirtyValues: false })
          }, 100)
        }
      } catch (e) {
        console.error('adminSkillBiteDeleteBlock', e)
        BannerStatic.show({ label: 'Error trying to delete a Block', isError: true })
        pageBodyOverlayRef.current.close()
      }
    }).catch(err => {
      console.log('Delete block cancelled', err)
    })
  }

  const handleAddBlock = async (type) => {
    if (!canEdit) return
    pageBodyOverlayRef.current.open('Adding block, please wait...')
    try {
      const payload = await addBlock({
        skillBiteId,
        sessionId: selectedSession.id,
        sectionId: selectedSection.id,
        pageId: selectedPage.id,
        type,
        order: selectedPage.blocks.length + 1
      })
      pageBodyOverlayRef.current.close()
      BannerStatic.show({ label: 'Skill Bite Block has been successfully created' })
      dispatch(SkillBiteEditSlice.actions.addBlock(payload))
      if (payload.pageId !== selectedPage.id) return

      append(payload.block)
      const wasFormDirty = form.formState.isDirty
      if (!wasFormDirty) {
        setTimeout(() => {
          form.reset(undefined, { keepValues: true, keepDirty: false, keepDirtyValues: false })
        }, 100)
      }
      onAddBlockSuccess()
    } catch (e) {
      console.error('adminSkillBiteInsertBlock', e)
      BannerStatic.show({ label: 'Error trying to add a Block', isError: true })
      pageBodyOverlayRef.current.close()
    }
  }

  const { fields, append } = useFieldArray({
    control: form.control,
    keyName: 'key',
    name: 'blocks'
  })

  const resetForm = (blocks) => {
    form.reset({ blocks: getBlocksWithDefaultValues(blocks) })
  }

  const handleUpdateBlocks = async ({ blocks }) => {
    if (!canEdit) return
    pageBodyOverlayRef.current.open('Updating page blocks, please wait...')
    try {
      const payload = await updateBlocks({
        skillBiteId,
        sessionId: selectedSession.id,
        sectionId: selectedSection.id,
        pageId: selectedPage.id,
        blocks: blocks
      })

      pageBodyOverlayRef.current.close()
      BannerStatic.show({ label: 'Skill Bite Page Blocks have been successfully updated' })
      dispatch(SkillBiteEditSlice.actions.updatePageBlocks(payload))
      resetForm(payload.blocks)
    } catch (e) {
      console.error('adminSkillBiteInsertBlock', e)
      BannerStatic.show({ label: 'Error updating page blocks', isError: true })
      pageBodyOverlayRef.current.close()
    }
  }

  return {
    deleteBlock: handleDelete,
    form,
    blocks: fields,
    resetForm,
    addBlock: handleAddBlock,
    updateBlocks: handleUpdateBlocks
  }
}
